import { useState, useEffect } from 'react';
import { Plus, FileText, Download, CheckCircle2, Clock, User, BookOpen } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import api from '../lib/api';
import { usePermissions } from '../components/Layout';

export default function BulletinsPage() {
  const { canCreate } = usePermissions();
  const [bulletins, setBulletins] = useState([]);
  const [students, setStudents] = useState([]);
  const [schoolYear, setSchoolYear] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [selectedType, setSelectedType] = useState('quranic');

  const [newBulletin, setNewBulletin] = useState({
    student_id: '', period: 'trimester1', bulletin_type: 'quranic',
    teacher_comment: '', director_comment: '', conduct_grade: 'A', absences: 0, delays: 0
  });

  useEffect(() => { fetchData(); }, [selectedType]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [bulletinsRes, yearRes] = await Promise.all([
        api.get('/bulletins', { params: { bulletin_type: selectedType } }),
        api.get('/school-years/active')
      ]);

      const studentType = selectedType === 'quranic' ? 'quranic' : 'classical';
      const studentsRes = await api.get('/students', { params: { student_type: studentType } });
      const mixedRes = await api.get('/students', { params: { student_type: 'mixed' } });

      setBulletins(bulletinsRes.data);
      setStudents([...studentsRes.data, ...mixedRes.data]);
      setSchoolYear(yearRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newBulletin.student_id) {
      toast.error('Veuillez sélectionner un élève');
      return;
    }
    setFormLoading(true);
    try {
      await api.post('/bulletins', { ...newBulletin, school_year_id: schoolYear?.id || '', bulletin_type: selectedType });
      toast.success('Bulletin créé');
      setShowDialog(false);
      setNewBulletin({ student_id: '', period: 'trimester1', bulletin_type: selectedType, teacher_comment: '', director_comment: '', conduct_grade: 'A', absences: 0, delays: 0 });
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la création');
    } finally {
      setFormLoading(false);
    }
  };

  const getStudentName = (id) => {
    const s = students.find(s => s.id === id);
    return s ? `${s.first_name} ${s.last_name}` : 'Inconnu';
  };

  const getPeriodLabel = (p) => ({ trimester1: '1er Trim.', trimester2: '2ème Trim.', trimester3: '3ème Trim.', annual: 'Annuel' }[p] || p);

  const getConductColor = (g) => ({ A: 'bg-green-100 text-green-700', B: 'bg-blue-100 text-blue-700', C: 'bg-amber-100 text-amber-700', D: 'bg-red-100 text-red-700' }[g] || 'bg-gray-100');

  const handleDownloadPdf = async (bulletinId) => {
    try {
      const response = await api.get(`/bulletins/${bulletinId}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `bulletin_${bulletinId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF téléchargé');
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="bulletins-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold">Bulletins</h1>
          <p className="text-muted-foreground text-sm">Consultez et téléchargez les bulletins scolaires</p>
        </div>
        {canCreate('bulletins') && (
          <Dialog open={showDialog} onOpenChange={setShowDialog}>
            <DialogTrigger asChild>
              <Button className="rounded-full" data-testid="create-bulletin-btn">
                <Plus className="w-4 h-4 mr-2" />Créer un bulletin
              </Button>
            </DialogTrigger>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Nouveau bulletin {selectedType === 'quranic' ? 'coranique' : 'classique'}</DialogTitle>
              <DialogDescription>Créer un bulletin pour un élève</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="space-y-2">
                <Label>Élève *</Label>
                <Select value={newBulletin.student_id} onValueChange={(v) => setNewBulletin({...newBulletin, student_id: v})}>
                  <SelectTrigger><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                  <SelectContent>
                    {students.map(s => <SelectItem key={s.id} value={s.id}>{s.first_name} {s.last_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Période</Label>
                  <Select value={newBulletin.period} onValueChange={(v) => setNewBulletin({...newBulletin, period: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="trimester1">1er Trimestre</SelectItem>
                      <SelectItem value="trimester2">2ème Trimestre</SelectItem>
                      <SelectItem value="trimester3">3ème Trimestre</SelectItem>
                      <SelectItem value="annual">Annuel</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Conduite</Label>
                  <Select value={newBulletin.conduct_grade} onValueChange={(v) => setNewBulletin({...newBulletin, conduct_grade: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="A">A - Excellent</SelectItem>
                      <SelectItem value="B">B - Bien</SelectItem>
                      <SelectItem value="C">C - Passable</SelectItem>
                      <SelectItem value="D">D - Insuffisant</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Absences</Label>
                  <input type="number" className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base" value={newBulletin.absences} onChange={(e) => setNewBulletin({...newBulletin, absences: parseInt(e.target.value) || 0})} />
                </div>
                <div className="space-y-2">
                  <Label>Retards</Label>
                  <input type="number" className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base" value={newBulletin.delays} onChange={(e) => setNewBulletin({...newBulletin, delays: parseInt(e.target.value) || 0})} />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Appréciation de l'enseignant</Label>
                <Textarea value={newBulletin.teacher_comment} onChange={(e) => setNewBulletin({...newBulletin, teacher_comment: e.target.value})} rows={2} placeholder="Commentaire..." />
              </div>
              <div className="space-y-2">
                <Label>Appréciation du directeur</Label>
                <Textarea value={newBulletin.director_comment} onChange={(e) => setNewBulletin({...newBulletin, director_comment: e.target.value})} rows={2} placeholder="Commentaire..." />
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>Annuler</Button>
                <Button type="submit" disabled={formLoading}>{formLoading ? 'Création...' : 'Créer'}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        )}
      </div>

      {/* Tabs */}
      <Tabs value={selectedType} onValueChange={setSelectedType} className="space-y-4">
        <TabsList>
          <TabsTrigger value="quranic" className="gap-2">
            <BookOpen className="w-4 h-4" />Bulletins Coraniques
          </TabsTrigger>
          <TabsTrigger value="classical" className="gap-2">
            <User className="w-4 h-4" />Bulletins Classiques
          </TabsTrigger>
        </TabsList>

        <TabsContent value={selectedType}>
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-48" />)}
            </div>
          ) : bulletins.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
                <p className="text-muted-foreground mb-4">Aucun bulletin créé</p>
                <Button variant="outline" className="rounded-full" onClick={() => setShowDialog(true)}>Créer un bulletin</Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {bulletins.map((b) => (
                <Card key={b.id} className="card-hover" data-testid={`bulletin-${b.id}`}>
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="font-semibold">{getStudentName(b.student_id)}</h3>
                        <p className="text-sm text-muted-foreground">{getPeriodLabel(b.period)}</p>
                      </div>
                      <Badge variant="outline" className={cn(b.is_validated ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700')}>
                        {b.is_validated ? <><CheckCircle2 className="w-3 h-3 mr-1" />Validé</> : <><Clock className="w-3 h-3 mr-1" />Brouillon</>}
                      </Badge>
                    </div>

                    <div className="grid grid-cols-3 gap-2 mb-4">
                      <div className="text-center p-2 bg-muted/50 rounded-lg">
                        <p className="text-lg font-bold">{b.general_average?.toFixed(1) || '-'}</p>
                        <p className="text-xs text-muted-foreground">Moyenne</p>
                      </div>
                      <div className="text-center p-2 bg-muted/50 rounded-lg">
                        <p className="text-lg font-bold">{b.rank || '-'}</p>
                        <p className="text-xs text-muted-foreground">Rang</p>
                      </div>
                      <div className="text-center p-2 bg-muted/50 rounded-lg">
                        <Badge className={cn("text-sm", getConductColor(b.conduct_grade))}>{b.conduct_grade}</Badge>
                        <p className="text-xs text-muted-foreground mt-1">Conduite</p>
                      </div>
                    </div>

                    <div className="flex items-center justify-between text-sm text-muted-foreground mb-3">
                      <span>{b.absences} absences</span>
                      <span>{b.delays} retards</span>
                    </div>

                    <Button 
                      variant="outline" 
                      className="w-full rounded-full" 
                      size="sm"
                      onClick={() => handleDownloadPdf(b.id)}
                      data-testid={`download-pdf-${b.id}`}
                    >
                      <Download className="w-4 h-4 mr-2" />Télécharger PDF
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
